import { type FC, useEffect } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router";
import { toSafeInt } from "widgets/utils";

import { isGenomeViewerEnabledEntity } from "@/app-utils/entity.ts";
import { useLoadDispatchWithIdAsNumber } from "@/app-utils/hooks/useLoadDispatchWithIdAsNumber.ts";
import { type ITranscriptState, shouldLoadTranscript } from "@/redux/modules/transcript.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { ENTITY_TYPE } from "@/settings/settings.js";

import GenericDetailPageLayout from "../core/navigation-panel/GenericDetailPageLayout.js";
import { type IRouteNavItemProps } from "../core/navigation-panel/RouteNavItem.js";
import TranscriptGenomeView from "./TranscriptGenomeView.js";
import TranscriptSummaryView from "./TranscriptSummaryView.js";

/**
 * The TranscriptPage smart component is called from the route corresponding to transcript/:transcriptId.
 * The children prop corresponds to the smart component injected from the nested routes (e.g. transcript/:transcriptId/summary)
 * The TranscriptPage will take care of filling up the basic transcript data and the nested child pages may request additional
 * data that is needed to show the nested page (such as transcript and transcript child collections for the summary page)
 */
const TranscriptDetailPage: FC = () => {
  const { transcriptId } = useParams<"transcriptId">();
  const { transcript, isLoading } = useSelector<IAppState, ITranscriptState>((state) => state.transcript);
  const load = useLoadDispatchWithIdAsNumber(shouldLoadTranscript, transcriptId);

  useEffect(() => {
    void load();
  }, [load]);

  const getNavItems = (): IRouteNavItemProps[] => {
    // enable genome viewer if there is at least one fitting location
    const viewableLocationAbsent = !transcript?.transcriptLocations
      .map((p) => p.region.contig)
      .find((c) => isGenomeViewerEnabledEntity(c.organism));
    const transcriptLocationsMissing: boolean = transcript && transcript.transcriptLocations.length === 0;
    const genomeviewDisabled = isLoading || transcriptLocationsMissing || viewableLocationAbsent;
    const genomeviewDec = {
      disabled: genomeviewDisabled,
      tooltip: transcriptLocationsMissing
        ? "Contig location has not been imported or calculated for this transcript"
        : viewableLocationAbsent
          ? "Transcript is not located on a genomic contig"
          : undefined,
    };

    return [
      {
        label: "Summary",
        route: "summary",
        pageComponent: <TranscriptSummaryView />,
        default: true,
        isLoading,
      },
      {
        label: "Genome View",
        disabled: genomeviewDec.disabled,
        disabledTooltip: genomeviewDec.tooltip,
        route: "genome",
        pageComponent: <TranscriptGenomeView />,
        isLoading,
      },
    ];
  };

  return (
    <GenericDetailPageLayout
      groupType={ENTITY_TYPE.TRANSCRIPT}
      entityId={toSafeInt(transcriptId)}
      getDisplayName={() => transcript?.sequenceId}
      sidebarNavItems={getNavItems()}
      isLoading={isLoading}
    />
  );
};

export default TranscriptDetailPage;
